import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getQueryString } from "../../utils/helper";
import { ManagerCourse } from "../../interfaces/manager.application";
import { getToken } from "../../utils/tokenStore";

const initialState = {
  loading: false,
  availableCourses: [] as ManagerCourse[],
  appliedCourses: [] as ManagerCourse[],
  myTraingHistories: [],
  updateLoading: false,
  availableCourseSessions: [] as any[],
  selectedCourse: {} as ManagerCourse,
  demoFile: null as string | null,
  waitingMessage: "",
  waitingError: null as string | null,
  waitingMessageLoading: false,
};

export const getLearnerAvailableApplications = createAsyncThunk(
  "learnerApplications/getLearnerAvailableApplications",
  async (_, { getState, rejectWithValue }) => {
    try {
      const query = getQueryString({
        user_id: sessionStorage.getItem("userId"),
        role: "learner",
        status: "LEARNER_AVAILABLE",
      });

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const fetchWaitingPosition = createAsyncThunk(
  "waitingPosition/fetch",
  async (
    { userId, courseId }: { userId: string; courseId: number },
    thunkAPI
  ) => {
    try {
      const token = getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}/waiting-position?user_id=${userId}&course_id=${courseId}`,
        {
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      ).then((response) => response.json());

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to fetch position"
      );
    }
  }
);

export const downloadDescriptionFile = createAsyncThunk(
  "learnerApplications/downloadDescriptionFile",
  async (props: { course_id: number }, { getState, rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_API_URL}/course-material?course_id=${props.course_id}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const getLearnerAppliedApplications = createAsyncThunk(
  "learnerApplications/getLearnerAppliedApplications",
  async (_, { getState, rejectWithValue }) => {
    try {
      const query = getQueryString({
        user_id: sessionStorage.getItem("userId"),
        role: "learner",
        status: "LEARNER_APPLIED",
      });

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const getLearnerMyTrainingHistory = createAsyncThunk(
  "learnerApplications/getLearnerMyTrainingHistory",
  async (_, { getState, rejectWithValue }) => {
    try {
      const query = getQueryString({
        user_id: sessionStorage.getItem("userId"),
        role: "learner",
        status: "LEARNER_PAST",
      });

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const getLearnerCourseSessions = createAsyncThunk(
  "learnerApplications/getCourseSessions",
  async (
    props: { course_id: string; role: string; user_id: any },
    { getState, rejectWithValue }
  ) => {
    try {
      const query = getQueryString(props);

      const url = `${process.env.REACT_APP_BASE_API_URL}/sessions${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const updateLearnerApplicationStatus = createAsyncThunk(
  "learnerApplications/updateLearnerApplicationStatus",
  async (
    props: {
      status: string;
      courseId: string | number;
      userId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const query = getQueryString({
        user_id: props.userId,
        role: "learner",
        course_id: props.courseId,
      });

      const url = `${process.env.REACT_APP_BASE_API_URL}/applications/${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "PUT",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          application_status: props.status,
        }),
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const learnerApplicationsSlice = createSlice({
  name: "learnerApplications",
  initialState,
  reducers: {
    setSelectedCourse: (state, action) => {
      state.selectedCourse = action.payload;
    },
    resetWaitingPosition(state) {
      state.waitingError = "";
      state.waitingMessage = "";
      state.waitingMessageLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLearnerAvailableApplications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLearnerAvailableApplications.fulfilled, (state, action) => {
        state.loading = false;
        // state.availableCourses = action.payload;
        // Initialize an empty array to hold transformed courses
        const transformedCourses: any = [];

        // Loop through each course in the action payload data
        action.payload.forEach((course: any) => {
          course.applications.forEach((application: any) => {
            transformedCourses.push({
              ...course,
              applications: [{ ...application }],
            });
          });
        });

        // Assign the transformed data to state
        state.availableCourses = transformedCourses;
      })
      .addCase(getLearnerAvailableApplications.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getLearnerAppliedApplications.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLearnerAppliedApplications.fulfilled, (state, action) => {
        state.loading = false;
        // state.appliedCourses = action.payload;
        // Initialize an empty array to hold transformed courses
        const transformedCourses: any = [];

        // Loop through each course in the action payload data
        action.payload.forEach((course: any) => {
          course.applications.forEach((application: any) => {
            transformedCourses.push({
              ...course,
              applications: [{ ...application }],
            });
          });
        });

        // Assign the transformed data to state
        state.appliedCourses = transformedCourses;
      })
      .addCase(fetchWaitingPosition.pending, (state) => {
        state.waitingMessageLoading = true;
        state.waitingMessage = "";
        state.waitingError = null;
      })
      .addCase(fetchWaitingPosition.fulfilled, (state, action) => {
        state.waitingMessageLoading = false;
        state.waitingMessage =
          action.payload?.message || "Waiting message fetched successfully";
        state.waitingError = null;
      })
      .addCase(fetchWaitingPosition.rejected, (state, action: any) => {
        state.waitingMessageLoading = false;
        state.waitingMessage = "";
        state.waitingError =
          action.payload?.message || "Failed to fetch waiting position";
      })
      .addCase(getLearnerAppliedApplications.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getLearnerMyTrainingHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLearnerMyTrainingHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.myTraingHistories = action.payload;
      })
      .addCase(getLearnerMyTrainingHistory.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getLearnerCourseSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getLearnerCourseSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.availableCourseSessions = action.payload || [];
      })
      .addCase(getLearnerCourseSessions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateLearnerApplicationStatus.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateLearnerApplicationStatus.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateLearnerApplicationStatus.rejected, (state) => {
        state.updateLoading = false;
      })
      .addCase(downloadDescriptionFile.pending, (state) => {
        state.loading = false;
      })
      .addCase(downloadDescriptionFile.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(downloadDescriptionFile.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export default learnerApplicationsSlice.reducer;

export const { setSelectedCourse, resetWaitingPosition } =
  learnerApplicationsSlice.actions;
